// @spec GHG-STR-016
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateCommutingSurveyForm } from '@/features/ghg/commuting-surveys/components';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/commuting-surveys/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.createCommutingSurvey'),
  }),
  component: CreateCommutingSurveyPage,
});

/**
 * Create new commuting survey page.
 *
 * Provides a form to configure a new employee commuting survey,
 * including settings for anonymity, deadline, reminders, and
 * target response rates.
 */
function CreateCommutingSurveyPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();

  const handleBack = () => {
    void navigate({ to: '/ghg/commuting-surveys' });
  };

  const handleSuccess = (surveyId: string) => {
    void navigate({ to: '/ghg/commuting-surveys/$surveyId', params: { surveyId } });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('commutingSurveysPage.back')}
          </Button>
          <h1 className="text-2xl font-semibold">{t('commutingSurveysPage.createHeading')}</h1>
          <p className="text-muted-foreground">
            {t('commutingSurveysPage.createDescription')}
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('commutingSurveysPage.surveyDetails')}</CardTitle>
            <CardDescription>
              {t('commutingSurveysPage.surveyDetailsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CreateCommutingSurveyForm onSuccess={handleSuccess} onCancel={handleBack} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
